import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Sparkles, Menu, X, ExternalLink } from "lucide-react";
import { NAV, HEADWAY } from "../data/site";
import InstallButton from "./InstallButton";

export default function Navbar({ bannerVisible }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname, location.hash]);

    const isActive = (to) =>
        to.startsWith("/#")
            ? false
            : location.pathname === to;

    const renderLink = (item) =>
        item.to.startsWith("/#") ? (
            <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-sm text-amber-100/80 transition-colors duration-200 hover:text-amber-300"
            >
                {item.label}
            </Link>
        ) : (
            <Link
                key={item.label}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-sm transition-colors duration-200 hover:text-amber-300 ${
                    isActive(item.to) ? "text-amber-300" : "text-amber-100/80"
                }`}
            >
                {item.label}
            </Link>
        );

    return (
        <header
            data-testid="navbar"
            className={`fixed inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
                scrolled
                    ? "border-b border-amber-500/15 bg-[#1a120c]/90 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                    : "border-b border-transparent bg-gradient-to-b from-black/50 to-transparent"
            }`}
            style={{ top: bannerVisible ? "var(--banner-h, 48px)" : "0px" }}
        >
            <nav className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-4">
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5"
                >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-500/40">
                        <Flame className="h-4 w-4 text-amber-400" />
                    </span>
                    <span className="font-serif text-lg tracking-tight text-amber-50">
                        NeuroPlay Counseling
                    </span>
                </Link>

                <div className="hidden items-center gap-6 lg:flex">
                    {NAV.map(renderLink)}
                    <Link
                        to="/how-to"
                        data-testid="nav-how-to"
                        className={`text-sm transition-colors duration-200 hover:text-amber-300 ${
                            isActive("/how-to") ? "text-amber-300" : "text-amber-100/80"
                        }`}
                    >
                        How-To
                    </Link>
                    <Link
                        to="/team-a7k2x9"
                        data-testid="nav-admin"
                        className={`text-sm transition-colors duration-200 hover:text-amber-300 ${
                            isActive("/team-a7k2x9") ? "text-amber-300" : "text-amber-100/80"
                        }`}
                    >
                        Admin
                    </Link>
                </div>

                <div className="hidden items-center gap-3 lg:flex">
                    <InstallButton variant="public" />
                    <a
                        href={HEADWAY}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-book-button"
                        className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-[#1F1510] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-amber-400"
                    >
                        <Sparkles className="h-4 w-4" />
                        Book Now
                        <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="flex h-10 w-10 items-center justify-center rounded-full text-amber-100 ring-1 ring-amber-500/25 lg:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden border-t border-amber-500/15 bg-[#1a120c]/98 backdrop-blur-md lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {NAV.map((item) => (
                                <Link
                                    key={item.label}
                                    to={item.to}
                                    className="rounded-lg px-3 py-2.5 text-sm text-amber-100/85 hover:bg-amber-500/10 hover:text-amber-200"
                                >
                                    {item.label}
                                </Link>
                            ))}
                            <Link
                                to="/how-to"
                                className="rounded-lg px-3 py-2.5 text-sm text-amber-100/85 hover:bg-amber-500/10 hover:text-amber-200"
                            >
                                How-To
                            </Link>
                            <Link
                                to="/team-a7k2x9"
                                className="rounded-lg px-3 py-2.5 text-sm text-amber-100/85 hover:bg-amber-500/10 hover:text-amber-200"
                            >
                                Admin
                            </Link>
                            <a
                                href={HEADWAY}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-3 text-sm font-semibold text-[#1F1510]"
                            >
                                <Sparkles className="h-4 w-4" /> Book Now
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
